import * as React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";

interface KPICardProps {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  delta?: { value: number; label?: string };
  sparkline?: React.ReactNode;
  accent?: "primary" | "allow" | "warn" | "deny";
  footer?: React.ReactNode;
  className?: string;
}

const accentRail: Record<NonNullable<KPICardProps["accent"]>, string> = {
  primary: "border-l-2 border-l-primary",
  allow: "border-l-2 border-l-status-allow",
  warn: "border-l-2 border-l-status-warning",
  deny: "border-l-2 border-l-status-deny",
};

export function KPICard({ icon, label, value, delta, sparkline, accent, footer, className }: KPICardProps) {
  const deltaPositive = delta && delta.value > 0;
  const deltaNeutral = delta && delta.value === 0;

  return (
    <Card
      className={cn(
        "rounded-lg border border-border bg-card shadow-elevation",
        accent && accentRail[accent],
        className
      )}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {icon && (
                <span className="text-muted-foreground">{icon}</span>
              )}
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {label}
              </span>
            </div>
            <div className="display tabular text-3xl font-semibold text-foreground leading-none">
              {value}
            </div>
            {delta !== undefined && (
              <div
                className={cn(
                  "flex items-center gap-1 mt-2 text-xs font-medium",
                  deltaNeutral ? "text-muted-foreground" : deltaPositive ? "text-status-allow" : "text-status-deny"
                )}
              >
                {deltaNeutral ? (
                  <Minus className="h-3 w-3" />
                ) : deltaPositive ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                <span className="tabular">
                  {deltaPositive ? "+" : ""}
                  {delta.value.toFixed(1)}%
                </span>
                {delta.label && <span className="text-muted-foreground font-normal">{delta.label}</span>}
              </div>
            )}
          </div>
          {sparkline && (
            <div className="shrink-0 opacity-80">{sparkline}</div>
          )}
        </div>
        {footer && (
          <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">{footer}</div>
        )}
      </CardContent>
    </Card>
  );
}
